import {Directive, effect, ElementRef, HostListener, inject, input, OnInit, Optional} from '@angular/core';
import {FormControlDirective} from '@angular/forms';
import {MainInjector} from '@shared-types';

import {SignalFormControl} from '../../signal-features';

/**
 * @example:
 *            <input
 *             [SignalFormControl]="field.signalFC"
 *             [formControl]="field.signalFC._fc"
 *             />
 */

@Directive({
	selector: 'input[SignalFormControl], textarea[SignalFormControl], select[SignalFormControl], mat-checkbox[SignalFormControl]',
	exportAs: 'SignalFormControl',
	standalone: true,
})
export class SignalFormControlDirective<TValue = string | boolean> implements OnInit {
	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	private readonly elementRef: ElementRef<HTMLInputElement> = inject(ElementRef);

	/* -----------------------------------------------  INPUTS ----------------------------------------------- */
	/**
	 * @description The SignalFormControl instance that is passed to the directive. This is a required input.
	 * @alias SignalFormControl
	 * @type {SignalFormControl<string | boolean>}
	 */
	readonly signalFC = input.required<SignalFormControl<TValue>>({alias: 'SignalFormControl'});

	/**
	 * @description This is the data passed to the directive from the component;
	 * @Optional
	 */
	readonly data = input<any>(undefined);

	/* ----------------------------------------------  CONSTRUCTOR ---------------------------------------------- */
	constructor(@Optional() protected readonly formControlDirective: FormControlDirective) {
		if (formControlDirective) {
			// if (isDevMode()) debugger;
		}
		// Effect listens to changes in the signalFC and updates the value of the element
		effect(
			() => {
				const value: TValue = this.signalFC().value();
				this._updateValue(value);
			},
			{
				injector: MainInjector,
			},
		);
	}

	/* --------------------------------------------  LIFECYCLE HOOKS -------------------------------------------- */
	ngOnInit(): void {
		// bind the initial value to the element
		const signalFC = this.signalFC();
		if (signalFC) {
			const value: TValue = signalFC.value();
			this._updateValue(value);
		}
	}

	/* -----------------------------------------------  LISTENERS ----------------------------------------------- */
	@HostListener('input', ['$event.target.value'])
	private _onInput(value: TValue): void {
		const inputElement: HTMLInputElement = this.elementRef.nativeElement;
		if (inputElement) this._updateValue(value);
	}

	/* --------------------------------------------  PRIVATE METHODS -------------------------------------------- */

	private _updateValue(value: TValue): void {
		if (typeof value === 'string') {
			this.elementRef.nativeElement.value = value;
			this.signalFC().setValue(value);
		} else if (typeof value === 'boolean') {
			this.elementRef.nativeElement.checked = value;
			this.signalFC().setValue(value);
		}
	}
}
